import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import type { UnpublishedState, PublishedSnapshot } from '@/types/appTypes'
import { fetchProductSettings, persistProductSettings } from '@/services/productSettingsService'

export const useProductSettingsStore = defineStore('productSettings', () => {
  const primarySources = ref<string[]>([])
  const primarySourcesInitialized = ref(false)
  // Indicates we have received at least one product-settings payload from the app container (or E2E seed).
  // Used to prevent "onboarding flash" and to gate bootstrapping work until we know real state.
  const hasLoaded = ref(false)
  const isOnBoarding = ref(true)
  const onboardingStep = ref<number>(1)
  // Local/session override to prevent the app-container from immediately snapping onboarding back to true
  // after the user completes onboarding. This can happen when the container keeps sending stale product
  // settings until downstream systems refresh.
  const forceOnboardingComplete = ref(false)
  // Dealer ops control: pause automatic feed-triggered publishes (defaults false if missing).
  const pausePublishByFeed = ref(false)
  // Deprecated: unpublished marker no longer persisted to JAMS. Use server-truth (ConfigDraft) instead.
  const unpublished = ref<UnpublishedState | null>(null)
  // Local-only: snapshot of configs at last publish time (not persisted to JAMS).
  const publishedSnapshot = ref<PublishedSnapshot | null>(null)
  // Preserve the last full `ise` config we received so partial saves don't clobber unrelated keys.
  const lastLoadedIseConfig = ref<Record<string, unknown> | null>(null)

  const appConfig = useJazelAppConfig()
  const accountSettings = useAccountSettingsStore()

  const getForceOnboardingCompleteKey = () => {
    const id = String(accountSettings.accountId || '').trim()
    return id ? `ise:onboarding:forceComplete:${id}` : null
  }

  const syncForceOnboardingCompleteFromSession = () => {
    const key = getForceOnboardingCompleteKey()
    if (!key) return
    try {
      forceOnboardingComplete.value = sessionStorage.getItem(key) === '1'
    } catch {
      // ignore
    }
  }

  const setIsOnBoarding = (value: boolean) => {
    isOnBoarding.value = value
    const key = getForceOnboardingCompleteKey()
    if (!key) return
    try {
      if (value === false) {
        forceOnboardingComplete.value = true
        sessionStorage.setItem(key, '1')
      } else {
        forceOnboardingComplete.value = false
        sessionStorage.removeItem(key)
      }
    } catch {
      // ignore
    }
  }

  const setOnboardingStep = (value: number) => {
    const n = Number(value) || 1
    onboardingStep.value = Math.max(1, Math.min(5, n))
  }

  const setPrimarySources = (sources: string[]) => {
    primarySources.value = sources
    primarySourcesInitialized.value = true
  }

  const setPausePublishByFeed = (value: boolean) => {
    pausePublishByFeed.value = !!value
  }

  const setProductSettings = async (settings: IAccountSettings | null) => {
    // Keep session override in sync once we know the account id.
    syncForceOnboardingCompleteFromSession()

    // Preserve the raw payload to support safe merge-on-save behavior.
    if (settings && typeof settings === 'object') {
      try {
        lastLoadedIseConfig.value = JSON.parse(JSON.stringify(settings)) as Record<string, unknown>
      } catch {
        lastLoadedIseConfig.value = { ...(settings as any) }
      }
    }

    // Set Account ID
    // IMPORTANT:
    // Some callers (notably the app container) may emit partial product settings objects.
    // Do NOT default `primarySources` to [] unless the field is explicitly present, otherwise
    // we'll overwrite the saved JAMS value on subsequent saves.
    if (settings && typeof settings === 'object' && 'primarySources' in (settings as any)) {
      const raw = (settings as any).primarySources
      if (Array.isArray(raw)) {
        setPrimarySources(raw.map(String).map((s) => s.trim()).filter(Boolean))
      } else if (typeof raw === 'string') {
        setPrimarySources(
          raw
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        )
      } else if (raw == null) {
        // Explicit null/undefined -> treat as empty list
        setPrimarySources([])
      }
    }

    // ISE setting: pause_publish_by_feed
    if (settings && typeof settings === 'object' && 'pause_publish_by_feed' in (settings as any)) {
      setPausePublishByFeed(!!(settings as any).pause_publish_by_feed)
    }
    // IMPORTANT:
    // Some callers (notably the app container) may emit partial product settings objects.
    // If `isOnBoarding` / `step` are missing, do NOT default back to "true/1" or the UI can
    // get stuck on onboarding even after a successful finish.
    if (typeof (settings as any)?.isOnBoarding === 'boolean') {
      const incoming = (settings as any).isOnBoarding as boolean
      // If the user completed onboarding locally, ignore stale container payloads that would snap back to true.
      if (!(incoming === true && forceOnboardingComplete.value === true)) {
        setIsOnBoarding(incoming)
      }
    }
    if (settings && typeof settings === 'object' && 'step' in (settings as any)) {
      setOnboardingStep(Number((settings as any).step) || 1)
    }
    // NOTE: unpublished + publishedSnapshot are no longer loaded from JAMS.
    // They're local-only or derived from server-truth (ConfigDraft).
    hasLoaded.value = true
  }

  const saveSettings = async (settings: any): Promise<boolean> => {
    const productId = 'ise'
    const incoming = settings && typeof settings === 'object' ? { ...(settings as any) } : {}

    // Read-before-write: fetch the current JAMS config so we never clobber keys
    // written by other processes (e.g. publish promotion writes primarySources,
    // feedMapping, etc.). Falls back to lastLoadedIseConfig if the read fails.
    //
    // Partial saves merge over this full baseline. Fields the caller omits — including
    // primarySources — are preserved from the baseline. Fields the caller explicitly
    // provides override the baseline. The JAMS endpoint is a full-config PUT, so
    // omitting an existing field from the payload would delete it.
    let base: Record<string, unknown> = {}
    try {
      const fresh = await fetchProductSettings(
        appConfig.jamsBackendUrl,
        accountSettings.accountId,
        productId,
        accountSettings.sessionToken,
      )
      if (fresh) {
        base = fresh
        lastLoadedIseConfig.value = JSON.parse(JSON.stringify(fresh))
      } else {
        base = lastLoadedIseConfig.value && typeof lastLoadedIseConfig.value === 'object'
          ? lastLoadedIseConfig.value
          : {}
      }
    } catch {
      base = lastLoadedIseConfig.value && typeof lastLoadedIseConfig.value === 'object'
        ? lastLoadedIseConfig.value
        : {}
    }

    settings = { ...(base as any), ...(incoming as any) }

    const ok = await persistProductSettings(
      appConfig.jamsBackendUrl,
      accountSettings.accountId,
      productId,
      accountSettings.sessionToken,
      {
        productName: productId,
        config: settings,
      },
    )
    // If the write succeeded, keep our local baseline in sync so future partial writes are safe.
    if (ok) {
      try {
        lastLoadedIseConfig.value = JSON.parse(JSON.stringify(settings)) as Record<string, unknown>
      } catch {
        lastLoadedIseConfig.value = settings as any
      }
    }
    return ok
  }

  /**
   * DEPRECATED: Unpublished markers are no longer persisted to JAMS.
   * Dirty state is derived from server-truth (ConfigDraft + VehicleDraft existence).
   * This method is kept for backward compatibility but does nothing.
   */
  const setUnpublished = async (payload: { source: string; details?: any; savedAt?: string }) => {
    console.warn('[productSettings] setUnpublished is deprecated (no-op). Use server-truth for dirty state.')
  }

  /**
   * DEPRECATED: No longer persists publishInitiatedAt to JAMS.
   */
  const setPublishInitiatedAt = async (isoString?: string) => {
    console.warn('[productSettings] setPublishInitiatedAt is deprecated (no-op).')
  }

  /**
   * DEPRECATED: No longer clears unpublished markers in JAMS.
   */
  const clearUnpublished = async () => {
    unpublished.value = null
    console.warn('[productSettings] clearUnpublished is deprecated (no-op). Use server-truth for dirty state.')
  }

  /**
   * DEPRECATED: No longer persists publishedSnapshot to JAMS.
   * Snapshot is kept local-only for UI comparison purposes.
   */
  const setPublishedSnapshot = async (snapshot: Partial<PublishedSnapshot>) => {
    const now = new Date().toISOString()
    publishedSnapshot.value = {
      lastPublishedAt: snapshot.lastPublishedAt ?? now,
      fieldMapping: snapshot.fieldMapping ?? publishedSnapshot.value?.fieldMapping ?? null,
      segments: snapshot.segments ?? publishedSnapshot.value?.segments ?? null,
      listingRules: snapshot.listingRules ?? publishedSnapshot.value?.listingRules ?? null,
    }
    console.warn('[productSettings] setPublishedSnapshot no longer persists to JAMS (local-only).')
  }

  /**
   * DEPRECATED: No longer persists unpublished marker to JAMS.
   */
  const persistUnpublished = async (next: UnpublishedState | null) => {
    unpublished.value = next
    console.warn('[productSettings] persistUnpublished is deprecated (no-op).')
  }

  /**
   * DEPRECATED: No longer removes unpublished sources from JAMS.
   */
  const removeUnpublishedSource = async (source: string) => {
    console.warn('[productSettings] removeUnpublishedSource is deprecated (no-op).')
  }

  /**
   * DEPRECATED: No longer updates unpublished source details in JAMS.
   */
  const setUnpublishedSourceDetails = async (source: string, details: any) => {
    console.warn('[productSettings] setUnpublishedSourceDetails is deprecated (no-op).')
  }

  return {
    primarySources,
    hasLoaded,
    isOnBoarding,
    onboardingStep,
    pausePublishByFeed,
    unpublished,
    publishedSnapshot,

    setPrimarySources,
    setPausePublishByFeed,
    setProductSettings,
    saveSettings,
    setIsOnBoarding,
    setOnboardingStep,
    setUnpublished,
    setPublishInitiatedAt,
    clearUnpublished,
    setPublishedSnapshot,
    persistUnpublished,
    removeUnpublishedSource,
    setUnpublishedSourceDetails,
  }
})
